import { ShieldAlert } from "lucide-react";
import type { PendenciaAdiantamentoDoMes, QuinzenaDaCompetencia } from "@/hooks/use-equipe";
import { formatarBRL, exibirPendenciasDoMes } from "@shared/equipe-fechamento";
import { AdiantamentoAcoes } from "./AdiantamentoAcoes";

/**
 * Pendência de adiantamento — o bloco vermelho ACIONÁVEL (migração 046).
 *
 * Fica onde o dano aconteceria (o fechamento do mês), e não numa tela
 * escondida. Vermelho e com botão, não faixa âmbar passiva: a faixa âmbar do
 * FecharQuinzenaSheet existia em agosto, foi lida, e o fechamento passou mesmo
 * assim — avisar sem oferecer a saída não impede nada.
 *
 * Movido de `DialogosFase2.tsx` para a aba "Mensalistas · mês" (Financeiro v3);
 * as três saídas (Gerar / Registrar pago por fora / Não houve) moram em
 * `AdiantamentoAcoes` — mesmo componente da linha e do extrato.
 */
export function BlocoPendencias({
  pendencias, competencia, quinzenaAberta, quinzena, competenciaTemQuinzena = true,
}: {
  pendencias: PendenciaAdiantamentoDoMes[];
  competencia: string;
  quinzenaAberta: boolean;
  /** A quinzena da competência, quando a tela a conhece — libera o "Gerar". */
  quinzena?: QuinzenaDaCompetencia | null;
  /**
   * `false` = nenhuma quinzena começa neste mês: não há pendência a mostrar
   * (nada a gerar, e a faixa de estado já explica). Só visibilidade — a trava
   * do servidor continua igual. Ver `exibirPendenciasDoMes`.
   */
  competenciaTemQuinzena?: boolean;
}) {
  if (!exibirPendenciasDoMes({ competenciaTemQuinzena, nPendencias: pendencias.length })) return null;

  return (
    <div className="rounded-lg border-2 border-erro/30 bg-erro-suave p-3 space-y-3" data-testid="bloco-pendencias-mes">
      <div className="flex items-start gap-2">
        <ShieldAlert className="h-4 w-4 text-erro shrink-0 mt-0.5" />
        <div className="text-xs text-erro">
          <p className="font-semibold">
            {pendencias.length === 1
              ? "1 mensalista sem adiantamento registrado nesta competência."
              : `${pendencias.length} mensalistas sem adiantamento registrado nesta competência.`}
          </p>
          <p className="mt-0.5">
            O fechamento vai pagar o <strong>salário cheio</strong>. Se o adiantamento foi pago por
            fora, registre aqui para abater.
            {quinzenaAberta && " A quinzena desta competência ainda está aberta — gerar resolve."}
          </p>
        </div>
      </div>

      {pendencias.map(p => (
        <div key={p.funcionarioId} className="rounded-md border border-erro/30 bg-card p-2.5 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-medium truncate">{p.apelido ?? p.nome}</span>
            <span className="text-sm font-semibold text-erro shrink-0">
              {formatarBRL(p.salarioMensal)}
            </span>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Adiantamento esperado: {formatarBRL(p.valorSugerido)} ({p.adiantamentoPct}%)
          </p>
          <AdiantamentoAcoes pendencia={p} competencia={competencia} quinzena={quinzena ?? null} />
        </div>
      ))}
    </div>
  );
}
